// utils/PDFGenerator.js
// Adaptation du générateur PDF du dossier modèle pour fonctionner sans dépendances locales,
// en chargeant jsPDF depuis un CDN au besoin. L’API de la classe reste identique.

class PDFGenerator {
  constructor() {
    this.doc = null;
    this.pageWidth = 210; // A4 mm
    this.pageHeight = 297; // A4 mm
    this.margin = 15; // Reduced margin for modern look
    this.currentY = this.margin;
    this.logoHeight = null;
    this.primaryColor = [79, 70, 229]; // Indigo-600 default
  }

  async loadFonts(doc) {
    if (doc.getFontList().Inter) return; // Already linked?
    // In browser context, we rely on standard fonts or addFileToVFS if we had the base64
    // For this improvement, let's stick to standard fonts but use 'helvetica' smartly
    // or load a font from URL if jsPDF supports it in this version.
    // We'll simulate a "Inter" feel by using Helvetica with specific spacing.
  }

  async ensureJsPDF() {
    if (typeof window !== 'undefined' && window.jspdf?.jsPDF) return window.jspdf.jsPDF;
    await new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-jspdf-cdn]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
      } else {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
        s.async = true;
        s.setAttribute('data-jspdf-cdn', 'true');
        s.onload = () => resolve();
        s.onerror = reject;
        document.body.appendChild(s);
      }
    });
    if (!window.jspdf?.jsPDF) throw new Error('jsPDF indisponible');
    return window.jspdf.jsPDF;
  }

  addPageNumbers() {
    const doc = this.doc;
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Page ${i}/${pageCount}`, this.pageWidth - this.margin, this.pageHeight - 8, { align: 'right' });
    }
  }

  async generateQuotePDF(quote, customer, vehicle, settings) {
    const JS = await this.ensureJsPDF();
    this.doc = new JS();
    this.currentY = this.margin;
    this.logoHeight = null;

    await this.addHeader(settings, 'DEVIS');
    this.addQuoteInfo(quote, customer, vehicle);
    this.addItemsTable(quote.items || []);
    this.addTotals(quote);
    this.addFooter(settings, 'quote');
    this.addPageNumbers();
    return this.doc;
  }

  async generateInvoicePDF(invoice, customer, vehicle, settings, installments = []) {
    const JS = await this.ensureJsPDF();
    this.doc = new JS();
    this.currentY = this.margin;
    this.logoHeight = null;

    await this.addHeader(settings, 'FACTURE');
    this.addInvoiceInfo(invoice, customer, vehicle);
    this.addItemsTable(invoice.items || []);
    this.addTotals(invoice);
    // Payment info must come right after totals
    this.addPaymentInfo(invoice, settings);
    // Then the installments schedule
    if (Array.isArray(installments) && installments.length) {
      this.addInstallmentsTable(installments, invoice);
    }
    this.addFooter(settings, 'invoice');
    this.addPageNumbers();
    return this.doc;
  }

  async generateContractPDF(contract, settings) {
    const JS = await this.ensureJsPDF();
    this.doc = new JS();
    this.currentY = this.margin;
    this.logoHeight = null;

    // 1. En-tête standard
    await this.addHeader(settings, 'CONTRAT DE PRESTATION');

    // 2. Infos Client & Projet
    this.addContractInfo(contract);

    // 3. Contenu du Contrat (Parsing du HTML)
    this.addContractContent(contract.content);

    // 4. Footer & Pagination
    this.addFooter(settings, 'quote');

    // 5. Signature (if exists)
    if (contract.signature) {
      await this.addSignature(contract.signature, contract.signed_at);
    }

    this.addPageNumbers();
    return this.doc;
  }

  async addSignature(signatureDataUrl, signedAt) {
    const doc = this.doc;
    const padding = 20;

    // Check if we need a new page for the signature
    if (this.currentY + 60 > this.pageHeight - 40) {
      doc.addPage();
      this.currentY = this.margin + 10;
    }

    this.currentY += 10;

    // Draw a box for the signature
    doc.setDrawColor(220, 220, 220);
    doc.setLineDash([2, 1], 0);
    doc.roundedRect(this.margin, this.currentY, 80, 45, 2, 2, 'D');
    doc.setLineDash([], 0);

    try {
      // Add the signature image
      const format = signatureDataUrl.includes('png') ? 'PNG' : 'JPEG';
      doc.addImage(signatureDataUrl, format, this.margin + 5, this.currentY + 5, 70, 30);
    } catch (e) {
      console.error("Signature image error:", e);
    }

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text('Signature électronique certifiée', this.margin, this.currentY + 50);
    doc.text(`Signé le : ${new Date(signedAt).toLocaleString()}`, this.margin, this.currentY + 54);
    doc.setTextColor(0, 0, 0);

    this.currentY += 60;
  }

  addContractInfo(contract) {
    const doc = this.doc;
    const customer = contract.expand?.customer;
    const project = contract.expand?.project;

    // Colonne Gauche : CLIENT
    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.text('CLIENT :', this.margin, this.currentY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    let y = this.currentY + 6;

    doc.text(customer?.name || 'Client', this.margin, y); y += 5;
    if (customer?.company) { doc.text(customer.company, this.margin, y); y += 5; }
    if (customer?.address) { doc.text(customer.address, this.margin, y); y += 5; }
    if (customer?.zip || customer?.city) { doc.text(`${customer.zip || ''} ${customer.city || ''}`, this.margin, y); y += 5; }

    // Colonne Droite : INFO CONTRAT
    const rightX = this.pageWidth - this.margin - 80;
    const topY = this.currentY;

    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.text('RÉFÉRENCES :', rightX, topY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    let ry = topY + 6;

    doc.text(`Titre : ${contract.title || 'Projet Web'}`, rightX, ry); ry += 5;
    if (contract.signed_at) {
      doc.text(`Signé le : ${this.formatDate(contract.signed_at)}`, rightX, ry); ry += 5;
    } else {
      doc.text(`Statut : Non signé`, rightX, ry); ry += 5;
    }
    const quoteRef = contract.expand?.quote?.quote_number;
    if (quoteRef) {
      doc.text(`Réf Devis : ${quoteRef}`, rightX, ry); ry += 5;
    }

    this.currentY = Math.max(y, ry) + 15;

    // Ligne de séparation
    doc.setDrawColor(220);
    doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
    this.currentY += 10;
  }

  addContractContent(html) {
    if (!html) return;
    const doc = this.doc;
    const maxWidth = this.pageWidth - (2 * this.margin);

    // Nettoyage basique des balises invisibles ou inutiles pour le texte pur
    // On remplace les balises de bloc par des marqueurs spéciaux pour splitter
    let text = html
      .replace(/<h3[^>]*>/gi, '||H3||')
      .replace(/<\/h3>/gi, '||/H3||')
      .replace(/<p[^>]*>/gi, '||P||')
      .replace(/<\/p>/gi, '||/P||')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<\/li>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/&nbsp;/g, ' ');

    // On supprime les autres tags HTML restants
    text = text.replace(/<[^>]+>/g, '');

    // On décode les entités HTML basiques
    text = text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&euro;/g, '€');

    // Parsing par blocs
    const parts = text.split('||H3||');

    parts.forEach((part) => {
      if (!part.trim()) return;

      const subParts = part.split('||/H3||');

      // C'est un Titre
      if (subParts.length > 1) {
        const title = subParts[0].trim();
        const body = subParts[1]; // Le reste (les paragraphes)

        // Check page break for Title
        if (this.currentY + 15 > this.pageHeight - 30) { doc.addPage(); this.currentY = this.margin; }

        // Render Title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(30, 41, 59); // Dark blue-grey
        doc.text(title, this.margin, this.currentY);
        this.currentY += 8;
        doc.setTextColor(0, 0, 0);

        // Render Body
        this.renderBodyText(body, maxWidth);
      } else {
        // C'est juste du texte sans titre (Intro ?)
        this.renderBodyText(subParts[0], maxWidth);
      }
    });

    // Special handling for Signature box if present in text logic, 
    // but the HTML parser above flattens structure.
    // The "Fait à..." and signature box are usually at the end.
    // We trust that renderBodyText handles the text flow.
    // To make the signature look cleaner, we might want to detect "Pou le Client" etc, but simple text flow is legible.
  }

  renderBodyText(rawText, maxWidth) {
    const doc = this.doc;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    // Split by our paragraph marker
    const paragraphs = rawText.split('||P||');

    paragraphs.forEach(p => {
      let cleanP = p.replace('||/P||', '').trim();
      if (!cleanP) return;

      const lines = doc.splitTextToSize(cleanP, maxWidth);

      // Check page break
      if (this.currentY + (lines.length * 5) > this.pageHeight - 20) {
        doc.addPage();
        this.currentY = this.margin;
      }

      lines.forEach(line => {
        doc.text(line, this.margin, this.currentY);
        this.currentY += 5;
      });

      this.currentY += 3; // Spacing between paragraphs
    });
    this.currentY += 5; // Spacing after section
  }

  async addHeader(settings, documentType) {
    const doc = this.doc;

    if (settings?.logo && settings?.id) {
      try {
        const fromSettings = settings?._baseUrl || '';
        const fromWindow = (typeof window !== 'undefined' && (window?.pb?.baseUrl || window?.POCKETBASE_URL)) || '';
        const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
        const apiBase = fromSettings || fromWindow || origin;
        const logoUrl = `${apiBase}/api/files/settings/${settings.id}/${settings.logo}`;
        const resp = await fetch(logoUrl, { mode: 'cors' });
        const blob = await resp.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
        // preserve aspect ratio based on real image dimensions
        const imgEl = await new Promise((resolve, reject) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = dataUrl;
        });
        const { width, height } = this.calculateLogoDimensions(imgEl, 50, 30); // mm limits
        const imageFormat = this.detectImageFormat(settings.logo);
        doc.addImage(dataUrl, imageFormat, this.margin, this.currentY, width, height);
        this.logoHeight = height;
      } catch (e) {
        // logo optionnel
      }
    }

    const leftX = this.margin + 55;
    const rightX = this.pageWidth - this.margin;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    const companyName = settings?.company_name || 'INFOCOLAB';
    doc.text(companyName, leftX, this.currentY + 5, { align: 'left' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let yLeft = this.currentY + 12;
    if (settings?.company_address) { doc.text(settings.company_address, leftX, yLeft, { align: 'left' }); yLeft += 5; }
    if (settings?.company_postal_code && settings?.company_city) { doc.text(`${settings.company_postal_code} ${settings.company_city}`, leftX, yLeft, { align: 'left' }); yLeft += 5; }
    if (settings?.company_phone) { doc.text(`Tél: ${settings.company_phone}`, leftX, yLeft, { align: 'left' }); yLeft += 5; }
    if (settings?.company_email) { doc.text(`Email: ${settings.company_email}`, leftX, yLeft, { align: 'left' }); yLeft += 5; }
    if (settings?.company_website) { doc.text(String(settings.company_website), leftX, yLeft, { align: 'left' }); yLeft += 5; }

    let yRight = this.currentY + 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Informations légales', rightX, yRight, { align: 'right' });
    yRight += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (settings?.siret) { doc.text(`SIRET: ${settings.siret}`, rightX, yRight, { align: 'right' }); yRight += 5; }
    if (settings?.tva_number) { doc.text(`N° TVA: ${settings.tva_number}`, rightX, yRight, { align: 'right' }); yRight += 5; }
    if (settings?.rcs) { doc.text(`RCS: ${settings.rcs}`, rightX, yRight, { align: 'right' }); yRight += 5; }

    const logoSpacing = this.logoHeight ? Math.max(this.logoHeight + 10, 40) : 40;
    this.currentY += logoSpacing;
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 82, 204);
    doc.text(documentType, this.pageWidth / 2, this.currentY, { align: 'center' });
    this.currentY += 15;
    doc.setTextColor(0, 0, 0);
  }

  addQuoteInfo(quote, customer, vehicle) {
    const doc = this.doc;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.text('CLIENT:', this.margin, this.currentY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    let yPosClient = this.currentY + 7;
    doc.text(customer?.name || quote.customer_name || 'Client inconnu', this.margin, yPosClient); yPosClient += 5;
    if (customer?.company) { doc.text(customer.company, this.margin, yPosClient); yPosClient += 5; }
    if (customer?.address) { const wrap = doc.splitTextToSize(String(customer.address), 80); wrap.forEach(l => { doc.text(l, this.margin, yPosClient); yPosClient += 5; }); }
    if (customer?.phone) { doc.text(`Tél: ${customer.phone}`, this.margin, yPosClient); yPosClient += 5; }
    if (customer?.email) { doc.text(`Email: ${customer.email}`, this.margin, yPosClient); yPosClient += 5; }

    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.text('DEVIS:', this.pageWidth - this.margin - 60, this.currentY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    let yPosQuote = this.currentY + 7;
    if (quote.quote_number) { doc.text(`N° ${quote.quote_number}`, this.pageWidth - this.margin - 60, yPosQuote); yPosQuote += 5; }
    if (quote.created) { doc.text(`Date: ${this.formatDate(quote.created)}`, this.pageWidth - this.margin - 60, yPosQuote); yPosQuote += 5; }
    if (quote.valid_until) { doc.text(`Valide jusqu'au: ${this.formatDate(quote.valid_until)}`, this.pageWidth - this.margin - 60, yPosQuote); yPosQuote += 5; }

    const topY = this.currentY + 50; this.currentY = topY;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.text('PROJET:', this.margin, this.currentY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    let yPosVehicle = this.currentY + 7;
    const vehicleInfo = `${vehicle?.brand || vehicle?.name || ''} ${vehicle?.model || ''}`.trim();
    if (vehicleInfo) { doc.text(vehicleInfo, this.margin, yPosVehicle); }
    const leftEndY = yPosVehicle;

    let rightEndY = topY;
    if (quote?.notes) {
      const rightWidth = 80; const rightX = this.pageWidth - this.margin - rightWidth;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('NOTES :', rightX, topY);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      const clean = String(quote.notes).replace(/<[^>]*>/g, '');
      const lines = doc.splitTextToSize(clean, rightWidth);
      let noteY = topY + 6; lines.forEach(line => { doc.text(line, rightX, noteY); noteY += 5; });
      rightEndY = noteY;
    }

    this.currentY = Math.max(leftEndY, rightEndY) + 18;
  }

  addInvoiceInfo(invoice, customer, vehicle) {
    const doc = this.doc;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.text('CLIENT:', this.margin, this.currentY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    let yPos = this.currentY + 7;
    doc.text(customer?.name || 'Client inconnu', this.margin, yPos); yPos += 5;
    if (customer?.company) { doc.text(customer.company, this.margin, yPos); yPos += 5; }
    if (customer?.address) { doc.text(customer.address, this.margin, yPos); yPos += 5; }
    if (customer?.phone) { doc.text(`Tél: ${customer.phone}`, this.margin, yPos); yPos += 5; }
    if (customer?.email) { doc.text(`Email: ${customer.email}`, this.margin, yPos); yPos += 5; }

    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.text('FACTURE:', this.pageWidth - this.margin - 60, this.currentY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    let metaY = this.currentY + 7;
    if (invoice.invoice_number) { doc.text(`N° ${invoice.invoice_number}`, this.pageWidth - this.margin - 60, metaY); metaY += 5; }
    if (invoice.created) { doc.text(`Date: ${this.formatDate(invoice.created)}`, this.pageWidth - this.margin - 60, metaY); metaY += 5; }
    if (invoice.due_date) { doc.text(`Échéance: ${this.formatDate(invoice.due_date)}`, this.pageWidth - this.margin - 60, metaY); metaY += 5; }

    const topY = this.currentY + 50; this.currentY = topY;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.text('PROJET:', this.margin, this.currentY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    let yV = this.currentY + 7;
    const vehicleInfo = `${vehicle?.brand || vehicle?.name || ''} ${vehicle?.model || ''}`.trim();
    if (vehicleInfo) { doc.text(vehicleInfo, this.margin, yV); }
    this.currentY = Math.max(yV, topY) + 18;
  }

  addItemsTable(items) {
    const doc = this.doc;

    // Modern Table Header
    doc.setFillColor(248, 250, 252); // Slate-50
    doc.setDrawColor(226, 232, 240); // Slate-200
    doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 10, 'FD');

    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139); // Slate-500
    doc.setFont('helvetica', 'bold');

    doc.text('DESCRIPTION', this.margin + 4, this.currentY + 6.5);
    doc.text('QTÉ', this.pageWidth - 75, this.currentY + 6.5, { align: 'center' });
    doc.text('PRIX UNIT.', this.pageWidth - 45, this.currentY + 6.5, { align: 'right' });
    doc.text('TOTAL', this.pageWidth - this.margin - 4, this.currentY + 6.5, { align: 'right' });

    this.currentY += 10;

    // Items Loop
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(15, 23, 42); // Slate-900
    const descWidth = this.pageWidth - this.margin * 2 - 80; // Space left for description

    items.forEach((item, index) => {
      const isEven = index % 2 === 0;
      const description = item.description || 'Prestation';
      const descLines = doc.splitTextToSize(description, descWidth);
      const lineHeight = 5;
      const rowHeight = Math.max(10, 8 + (descLines.length * lineHeight));

      // Page Break Check
      if (this.currentY + rowHeight > this.pageHeight - 40) {
        doc.addPage();
        this.currentY = this.margin + 10; // Reset Y with space
        // Re-draw header? Optional, simplified for now
      }

      // Zebra striping optional
      // if (!isEven) { doc.setFillColor(250, 250, 255); doc.rect(this.margin, this.currentY, this.pageWidth - 2*this.margin, rowHeight, 'F'); }

      // Description vertical center or top
      let textY = this.currentY + 7;
      descLines.forEach(line => {
        doc.text(line, this.margin + 4, textY);
        textY += lineHeight;
      });

      // Values
      const centerY = this.currentY + 7;
      doc.text(String(item.quantity || 1), this.pageWidth - 75, centerY, { align: 'center' });
      doc.text(`${Number(item.unit_price || 0).toFixed(2)} €`, this.pageWidth - 45, centerY, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.text(`${(Number(item.quantity || 1) * Number(item.unit_price || 0)).toFixed(2)} €`, this.pageWidth - this.margin - 4, centerY, { align: 'right' });
      doc.setFont('helvetica', 'normal');

      // Thin separator
      doc.setDrawColor(241, 245, 249);
      doc.line(this.margin, this.currentY + rowHeight, this.pageWidth - this.margin, this.currentY + rowHeight);

      this.currentY += rowHeight;
    });

    this.currentY += 5;
  }

  addTotals(document) {
    const doc = this.doc;
    const boxWidth = 90;
    const startX = this.pageWidth - this.margin - boxWidth;

    // Background box for totals
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    // Rough height estimate
    doc.roundedRect(startX, this.currentY, boxWidth, 45, 3, 3, 'FD');

    let innerY = this.currentY + 8;
    const rightColX = this.pageWidth - this.margin - 6;
    const labelColX = startX + 6;

    doc.setFontSize(9);

    // Subtotal
    doc.setTextColor(100, 116, 139);
    doc.text('Sous-total HT', labelColX, innerY);
    doc.setTextColor(15, 23, 42);
    doc.text(`${(document.subtotal || 0).toFixed(2)} €`, rightColX, innerY, { align: 'right' });
    innerY += 7;

    // Discount
    if (document.discount_total > 0) {
      doc.setTextColor(22, 163, 74); // Green
      doc.text(`Remise (${document.discount_label || ''})`, labelColX, innerY);
      doc.text(`- ${(document.discount_total || 0).toFixed(2)} €`, rightColX, innerY, { align: 'right' });
      innerY += 7;
    }

    // Tax
    doc.setTextColor(100, 116, 139);
    if (document.tax_enabled !== false) {
      doc.text(`TVA (${document.tax_rate || 20}%)`, labelColX, innerY);
      doc.text(`${(document.tax_amount || 0).toFixed(2)} €`, rightColX, innerY, { align: 'right' });
      innerY += 10;
    } else {
      doc.text(`TVA non applicable`, labelColX, innerY);
      innerY += 10;
    }

    // Separator line
    doc.setDrawColor(203, 213, 225);
    doc.line(labelColX, innerY - 4, rightColX, innerY - 4);

    // Total TTC
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(79, 70, 229); // Primary color
    doc.text('Total TTC', labelColX, innerY);
    doc.text(`${(document.total || 0).toFixed(2)} €`, rightColX, innerY, { align: 'right' });

    this.currentY += 55; // Push down for next section
  }

  addInstallmentsTable(installments, invoice) {
    const doc = this.doc;
    // Ensure space, else new page
    const needed = 12 + Math.max(installments.length, 1) * 8 + 10;
    if (this.currentY > this.pageHeight - (needed + 40)) { doc.addPage(); this.currentY = this.margin; }

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Échéancier de paiement', this.margin, this.currentY);
    this.currentY += 8;

    // Header
    doc.setFillColor(240, 240, 240);
    doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, 8, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    const cols = {
      n: this.margin + 2,
      label: this.margin + 12,
      date: this.pageWidth - 120,
      amount: this.pageWidth - 85,
      paid: this.pageWidth - 55,
      status: this.pageWidth - this.margin - 2,
    };
    doc.text('#', cols.n, this.currentY + 5);
    doc.text('Échéance', cols.label, this.currentY + 5);
    doc.text('Date', cols.date, this.currentY + 5);
    doc.text('Montant TTC', cols.amount, this.currentY + 5, { align: 'right' });
    doc.text('Payé', cols.paid, this.currentY + 5, { align: 'right' });
    doc.text('Statut', cols.status, this.currentY + 5, { align: 'right' });
    this.currentY += 10;

    // Rows
    doc.setFont('helvetica', 'normal');
    installments.forEach((it, idx) => {
      const y = this.currentY + 6;
      doc.text(String(idx + 1), cols.n, y);
      doc.text(String(it.label || `Échéance ${idx + 1}`), cols.label, y);
      doc.text(this.formatDate(it.due_date), cols.date, y);
      doc.text(`${Number(it.amount_due || 0).toFixed(2)}€`, cols.amount, y, { align: 'right' });
      doc.text(`${Number(it.amount_paid || 0).toFixed(2)}€`, cols.paid, y, { align: 'right' });
      const stRaw = String(it.status || 'pending');
      const stMap = { pending: 'En attente', partial: 'Partiel', paid: 'Payé', overdue: 'En retard' };
      const st = stMap[stRaw] || stRaw;
      doc.text(st, cols.status, y, { align: 'right' });
      this.currentY += 8;
      if (this.currentY > this.pageHeight - 50) { doc.addPage(); this.currentY = this.margin; }
    });

    // Summary
    const total = Number(invoice?.total || 0);
    const totalPaid = installments.reduce((s, i) => s + Number(i.amount_paid || 0), 0);
    const balance = Math.max(0, total - totalPaid);

    this.currentY += 4;
    const sx = this.pageWidth - 80;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text('Total payé:', sx, this.currentY);
    doc.text(`${totalPaid.toFixed(2)}€`, this.pageWidth - this.margin - 2, this.currentY, { align: 'right' });
    this.currentY += 7;
    doc.text('Reste à payer:', sx, this.currentY);
    doc.text(`${balance.toFixed(2)}€`, this.pageWidth - this.margin - 2, this.currentY, { align: 'right' });
    this.currentY += 12;
  }

  addPaymentInfo(invoice, settings) {
    const doc = this.doc;
    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.text('MODALITÉS DE PAIEMENT:', this.margin, this.currentY);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); this.currentY += 8;
    const paymentTerms = settings?.invoice_payment_terms || 30;
    doc.text(`Paiement à ${paymentTerms} jours`, this.margin, this.currentY); this.currentY += 5;
    if (settings?.bank_name && settings?.bank_iban) { doc.text(`Banque: ${settings.bank_name}`, this.margin, this.currentY); this.currentY += 5; doc.text(`IBAN: ${settings.bank_iban}`, this.margin, this.currentY); this.currentY += 5; if (settings.bank_bic) { doc.text(`BIC: ${settings.bank_bic}`, this.margin, this.currentY); this.currentY += 5; } }
    this.currentY += 10;
  }

  addFooter(settings, documentType) {
    const doc = this.doc;
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    const centerX = this.pageWidth / 2; const footerY = this.pageHeight - 30;
    const footerText = documentType === 'quote' ? settings?.quote_footer : settings?.invoice_footer;
    if (footerText) {
      const lines = doc.splitTextToSize(String(footerText).replace(/<[^>]*>/g, ''), this.pageWidth - 2 * this.margin);
      let y = footerY + 5; lines.forEach(line => { doc.text(line, centerX, y, { align: 'center' }); y += 4.5; });
    }
  }

  formatDate(dateString) { if (!dateString) return ''; const d = new Date(dateString); return d.toLocaleDateString('fr-FR'); }

  loadImage(url) { return new Promise((resolve, reject) => { const img = new Image(); img.crossOrigin = 'anonymous'; img.onload = () => resolve(img); img.onerror = reject; img.src = url; }); }

  calculateLogoDimensions(img, maxWidth, maxHeight) {
    const originalWidth = img.naturalWidth || img.width; const originalHeight = img.naturalHeight || img.height;
    if (!originalWidth || !originalHeight) return { width: maxWidth * 0.8, height: maxHeight * 0.8 };
    const aspectRatio = originalWidth / originalHeight;
    let newWidth = maxWidth; let newHeight = maxWidth / aspectRatio;
    if (newHeight > maxHeight) { newHeight = maxHeight; newWidth = maxHeight * aspectRatio; }
    return { width: newWidth, height: newHeight };
  }

  detectImageFormat(filename) { if (!filename) return 'JPEG'; const ext = filename.toLowerCase().split('.').pop(); if (ext === 'png') return 'PNG'; if (ext === 'jpg' || ext === 'jpeg') return 'JPEG'; if (ext === 'gif') return 'GIF'; if (ext === 'webp') return 'WEBP'; return 'JPEG'; }

  download(filename) { if (this.doc) { this.doc.save(filename); } }
  preview() { if (this.doc) { const pdfBlob = this.doc.output('blob'); const url = URL.createObjectURL(pdfBlob); window.open(url, '_blank'); } }
  getBlob() { if (this.doc) { return this.doc.output('blob'); } return null; }
}

export default PDFGenerator;
